import React, { useMemo } from 'react';

// Compares teams at a glance: each team's average punt per kriteria over all beoordelaars.
// Clicking a team selects it, so the page can show what every beoordelaar gave it.
// Works only on data the page has already loaded - it makes no requests of its own.

export const thStyle = {
  padding: '5px',
  borderBottom: '2px solid #0e1e3b',
  color: '#0e1e3b',
  textAlign: 'center',
  verticalAlign: 'bottom',
  fontSize: '0.8rem'
};

export const tdStyle = {
  padding: '4px 6px',
  borderBottom: '1px solid #e0e0e0',
  textAlign: 'center',
  whiteSpace: 'nowrap'
};

export const formatPunt = (value) => value.toFixed(1);

// Darker shade for a higher share of the maximum; the number itself is always shown
export const shade = (fraction) => {
  const clamped = Math.min(Math.max(fraction, 0), 1);
  return `rgba(119, 80, 45, ${(0.08 + 0.42 * clamped).toFixed(2)})`;
};

function SpanVergelyking({ teams, punte, merkblads, selectedSpanId, onSelectSpan }) {
  const rows = useMemo(() => {
    // span_id -> { kriteria: { kriteria_id -> { sum, count } }, beoordelaars: Set }
    const byTeam = {};
    punte.forEach(punt => {
      if (!byTeam[punt.span_id]) {
        byTeam[punt.span_id] = { kriteria: {}, beoordelaars: new Set() };
      }
      const team = byTeam[punt.span_id];
      if (punt.beoordelaar) team.beoordelaars.add(punt.beoordelaar);
      if (!team.kriteria[punt.kriteria_id]) {
        team.kriteria[punt.kriteria_id] = { sum: 0, count: 0 };
      }
      team.kriteria[punt.kriteria_id].sum += punt.punt;
      team.kriteria[punt.kriteria_id].count += 1;
    });

    return teams
      .map(team => {
        const scored = byTeam[team.span_id];
        const averages = {};
        let total = 0;
        let totalMax = 0;
        merkblads.forEach(merkblad => {
          const kriteria = scored && scored.kriteria[merkblad.kriteria_id];
          if (kriteria && kriteria.count > 0) {
            const average = kriteria.sum / kriteria.count;
            averages[merkblad.kriteria_id] = average;
            total += average;
            totalMax += merkblad.totaal || 0;
          }
        });
        return {
          span_id: team.span_id,
          naam: team.naam,
          averages,
          total,
          totalMax,
          percentage: totalMax > 0 ? (total / totalMax) * 100 : null,
          beoordelaars: scored ? scored.beoordelaars.size : 0
        };
      })
      // Highest total first; teams without punte at the bottom, alphabetically
      .sort((a, b) => {
        if (a.percentage === null && b.percentage === null) return a.naam.localeCompare(b.naam);
        if (a.percentage === null) return 1;
        if (b.percentage === null) return -1;
        return b.percentage - a.percentage;
      });
  }, [teams, punte, merkblads]);

  // Highest average per kriteria, to mark the best team in each column
  const bestByKriteria = useMemo(() => {
    const best = {};
    merkblads.forEach(merkblad => {
      const values = rows
        .map(row => row.averages[merkblad.kriteria_id])
        .filter(value => value !== undefined);
      if (values.length > 0) best[merkblad.kriteria_id] = Math.max(...values);
    });
    return best;
  }, [rows, merkblads]);

  if (teams.length === 0) {
    return <p style={{ color: '#666' }}>Geen spanne nie.</p>;
  }
  if (merkblads.length === 0) {
    return <p style={{ color: '#666' }}>Geen kriteria op die merkblad vir hierdie rondte nie.</p>;
  }

  const toggleSpan = (spanId) => onSelectSpan(spanId === selectedSpanId ? null : spanId);

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <p style={{ color: '#666', margin: '0 0 0.5rem 0' }}>
        Gemiddelde punt per kriteria oor alle beoordelaars. ★ is die hoogste gemiddeld in elke kriteria.
        Klik op 'n span om te sien wat elke beoordelaar gegee het.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', backgroundColor: 'white', fontSize: '0.9rem' }}>
          <thead>
            <tr>
              <th style={thStyle}>#</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Span</th>
              {merkblads.map(merkblad => (
                <th
                  key={merkblad.merkblad_id}
                  style={{ ...thStyle, minWidth: '60px', maxWidth: '140px' }}
                  title={merkblad.kriteria_beskrywing}
                >
                  {merkblad.kriteria_beskrywing}
                  <div style={{ fontWeight: 'normal', color: '#666' }}>/ {merkblad.totaal}</div>
                </th>
              ))}
              <th style={thStyle}>Totaal</th>
              <th style={thStyle}>Beoordelaars</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const selected = row.span_id === selectedSpanId;
              return (
                <tr
                  key={row.span_id}
                  onClick={() => toggleSpan(row.span_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleSpan(row.span_id);
                    }
                  }}
                  tabIndex={0}
                  aria-selected={selected}
                  title={`Wys wat elke beoordelaar vir ${row.naam} gegee het`}
                  style={{ cursor: 'pointer', backgroundColor: selected ? '#fff8e1' : 'white' }}
                >
                  <td style={tdStyle}>{row.percentage === null ? '' : index + 1}</td>
                  <td style={{
                    ...tdStyle,
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: '#0e1e3b',
                    borderLeft: selected ? '5px solid #77502d' : '5px solid transparent'
                  }}>
                    {row.naam}
                  </td>
                  {merkblads.map(merkblad => {
                    const average = row.averages[merkblad.kriteria_id];
                    if (average === undefined) {
                      return <td key={merkblad.merkblad_id} style={{ ...tdStyle, color: '#999' }}>-</td>;
                    }
                    const isBest = average === bestByKriteria[merkblad.kriteria_id];
                    return (
                      <td
                        key={merkblad.merkblad_id}
                        title={`${formatPunt(average)} / ${merkblad.totaal}`}
                        style={{
                          ...tdStyle,
                          backgroundColor: shade(merkblad.totaal ? average / merkblad.totaal : 0),
                          fontWeight: isBest ? 'bold' : 'normal'
                        }}
                      >
                        {formatPunt(average)}{isBest ? ' ★' : ''}
                      </td>
                    );
                  })}
                  <td style={{ ...tdStyle, fontWeight: 'bold' }}>
                    {row.percentage === null
                      ? '-'
                      : `${formatPunt(row.total)} / ${row.totalMax} (${Math.round(row.percentage)}%)`}
                  </td>
                  <td style={tdStyle}>{row.beoordelaars}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SpanVergelyking;
